import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Text from '../components/elements/TextComponent';
import ImageComponent from '../components/elements/ImageComponent';
import Button from '../components/elements/Button';
import userAtDesk from '../assets/userAtDesk.svg';

const PRODUCT_NAME = 'ComeChatWithMe.ai';
const EFFECTIVE_DATE = 'July 30, 2025';
const CONTACT_EMAIL = 'comechatwithmeai@gmail.com';
const GOVERNING_LAW = 'State of Israel';

const MobileTerms: React.FC = () => {
  const navigate = useNavigate();
  const [imageSize, setImageSize] = useState(160);

  useEffect(() => {
    const update = () => {
      const vh = window.visualViewport?.height || window.innerHeight;
      const w = window.innerWidth;
      const size = Math.min(w * 0.45, vh * 0.3);
      setImageSize(Math.max(140, Math.min(size, 220)));
    };

    update();
    window.visualViewport?.addEventListener('resize', update);
    window.addEventListener('resize', update);
    return () => {
      window.visualViewport?.removeEventListener('resize', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Montserrat',
      }}
    >
      {/* Purple top section */}
      <div
        style={{
          backgroundColor: 'var(--primary)',
          padding: '24px 16px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <ImageComponent
          src={userAtDesk}
          center
          style={{ width: `${imageSize}px`, height: `${imageSize}px`, objectFit: 'contain' }}
        />
        <Text
          text={PRODUCT_NAME}
          fontSize="24px"
          fontWeight="700"
          color="#ffffff"
          textAlign="center"
          navigateToHome
        />
      </div>

      {/* Scrollable white content */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px 20px 160px',
          backgroundColor: '#ffffff',
          WebkitOverflowScrolling: 'touch',
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
        }}
      >
        <Text text="Terms of Service" fontSize="24px" fontWeight="700" color="#030303" textAlign="left" />
        <Text
          text={`Effective Date: ${EFFECTIVE_DATE}`}
          fontSize="14px"
          fontWeight="400"
          color="#4a4a4a"
          textAlign="left"
        />
        <Text
          text={`Contact: ${CONTACT_EMAIL}`}
          fontSize="14px"
          fontWeight="400"
          color="#4a4a4a"
          textAlign="left"
        />

        <div style={{ marginTop: 20 }}>
          {sections.map(({ title, bullets, body }, i) => (
            <div key={i} style={{ marginBottom: 20 }}>
              <Text
                text={title}
                fontSize="18px"
                fontWeight="700"
                color="#030303"
                textAlign="left"
              />
              <div style={{ marginTop: 8 }}>
                {body && (
                  <Text
                    text={body}
                    fontSize="14px"
                    fontWeight="400"
                    lineHeight="22px"
                    color="#030303"
                    textAlign="left"
                  />
                )}
                {bullets &&
                  bullets.map((b, j) => (
                    <div key={j} style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                      <Text text="•" fontSize="14px" fontWeight="700" color="#030303" textAlign="left" />
                      <Text
                        text={b}
                        fontSize="14px"
                        fontWeight="400"
                        lineHeight="22px"
                        color="#030303"
                        textAlign="left"
                      />
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <Button
            backgroundColor="var(--primary)"
            color="#ffffff"
            label="Back to Login"
            onClick={() => navigate('/login')}
          />
          <Button
            backgroundColor="#eaeaea"
            color="#030303"
            label="Privacy Policy"
            onClick={() => navigate('/privacypolicy')}
          />
        </div>
      </div>
    </div>
  );
};

const sections = [
  { title: '1. Agreement & Scope', body: `${PRODUCT_NAME} provides tools for creating and sharing chatbots. By accessing or using the Platform, you agree to these Terms. If you do not agree, do not use the Platform.` },
  { title: '2. Accounts & Eligibility', bullets: [
    'Provide accurate information and keep your account secure.',
    'You must be at least 13 years old (or the age of consent in your jurisdiction).',
  ] },
  { title: '3. Subscriptions, Billing & Cancellations', bullets: [
    'Monthly subscription fees grant access to premium features. Payments are processed by third-party processors; we do not store card numbers.',
    'Subscriptions auto-renew unless canceled before the renewal date. You can cancel at any time in your account settings (or by contacting support).',
    'Refunds are handled in our discretion and where required by law.',
  ] },
  { title: '4. Visitor Conversations', bullets: [
    'Visitors/recruiters acknowledge that their chat messages and timestamps will be visible to the bot owner.',
    'Bot owners are responsible for how they use the conversation data.',
  ] },
  { title: '5. Acceptable Use', bullets: [
    'Do not upload or share illegal, harmful, or infringing content.',
    'Do not harass, spam, or misuse the Platform.',
    'Do not attempt to bypass security or disrupt service.',
  ] },
  { title: '6. Intellectual Property', bullets: [
    'We retain all rights to the Platform. You retain ownership of your uploaded content, but grant us a limited license to host and process it to provide the service.',
  ] },
  { title: '7. Privacy', body: 'Your use of the Platform is subject to our Privacy Policy. Please review it for details on how we collect and use data.' },
  { title: '8. Third-Party Services', body: 'We may rely on third-party providers (e.g., hosting, analytics, payment processors). We are not responsible for their services, but we take reasonable steps to work with reputable providers.' },
  { title: '9. Disclaimers', body: 'The Platform is provided “as is” and “as available.” We make no warranties of fitness for a particular purpose, merchantability, or non-infringement to the maximum extent permitted by law.' },
  { title: '10. Limitation of Liability', body: 'To the maximum extent permitted by law, we are not liable for indirect, incidental, special, consequential, or exemplary damages. Our total liability for any claim is limited to the amount you paid in the 12 months preceding the event.' },
  { title: '11. Indemnity', body: 'You agree to indemnify and hold us harmless from claims arising out of your content, your use of the Platform, or your violation of these Terms.' },
  { title: '12. Termination', body: 'We may suspend or terminate your access if you violate these Terms, pose a risk, or as required by law. You may stop using the Platform at any time.' },
  { title: '13. Changes to Terms', body: 'We may update these Terms from time to time. If changes are material, we will notify you via email or in-app notice. Continued use constitutes acceptance.' },
  { title: '14. Governing Law', body: `These Terms are governed by the laws of the ${GOVERNING_LAW}, without regard to conflict of law principles. Venue and jurisdiction will be in the competent courts of that jurisdiction.` },
  { title: '15. Contact', body: `Questions? Contact ${CONTACT_EMAIL}.` },
];

export default MobileTerms;
